<template>
  <div data-private>
  <PageHeader title="Call" :breadcrumbs="[{ label: 'Beta' }, { label: 'Outreach' }]" />

  <div class="p-4">

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div class="lg:col-span-2 space-y-6">
        <!-- New Call Form -->
        <CallCreateForm
          :configurations="configurations"
          @call-started="handleCallStarted"
        />

        <!-- Call Configurations -->
        <CallConfigurationsCard
          :configurations="configurations"
          @configurations-updated="configurations = $event"
          @open-config-modal="openConfigModal"
        />

        <!-- Call History -->
        <CallHistoryCard
          ref="callHistoryCard"
          @row-clicked="handleCallClicked"
        />
      </div>

      <!-- Right Column: Details Pane -->
      <div class="lg:col-span-1">
        <CallDetailsCard
          :selected-call="selectedCall"
          @open-config-modal="openConfigModal"
          @summary-generated="handleSummaryGenerated"
        />
      </div>
    </div>

    <!-- Call Configuration Modal -->
    <CallConfigurationModal
      v-model="isConfigModalOpen"
      :config="selectedConfig"
      :agents="agents"
      @save="handleSaveConfig"
    />
  </div>
  </div>
</template>

<script>
import axios from 'axios'
import CallCreateForm from './Call/CallCreateForm.vue'
import CallConfigurationsCard from './Call/CallConfigurationsCard.vue'
import CallHistoryCard from './Call/CallHistoryCard.vue'
import CallDetailsCard from './Call/CallDetailsCard.vue'
import CallConfigurationModal from './Call/Modals/CallConfigurationModal.vue'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'CallTab',
  components: {
    CallCreateForm,
    CallConfigurationsCard,
    CallHistoryCard,
    CallDetailsCard,
    CallConfigurationModal,
    PageHeader,
  },
  data() {
    return {
      configurations: [],
      agents: [],
      selectedCall: null,
      isConfigModalOpen: false,
      selectedConfig: null,
    }
  },
  computed: {
    toast() {
      return useToast()
    },
  },
  async mounted() {
    try {
      const [configsResponse, agentsResponse] = await Promise.all([
        axios.get('api/outreach/call-configurations/'),
        axios.get('api/outreach/agents/'),
      ])
      // Handle paginated responses
      this.configurations = configsResponse.data.results || configsResponse.data
      this.agents = agentsResponse.data.results || agentsResponse.data
      // Call history is now loaded via AG Grid server-side datasource
    } catch (error) {
      console.error('Failed to fetch data:', error)
    }
  },
  methods: {
    async handleCallClicked(event) {
      const callId = event.data.id
      if (!callId) return

      try {
        // Fetch full call details from individual endpoint
        const response = await axios.get(`api/outreach/calls/${callId}/`)
        this.selectedCall = response.data
      } catch (error) {
        console.error('Failed to fetch call details:', error)
        this.toast.error('Failed to load call details')
        // Fall back to grid data
        this.selectedCall = event.data
      }
    },
    async handleCallStarted(newCall) {
      // Refresh the call history grid
      if (this.$refs.callHistoryCard?.gridApi) {
        this.$refs.callHistoryCard.gridApi.refreshServerSide({ purge: true })
      }
    },
    handleSummaryGenerated(updatedCall) {
      // Update selected call if it matches
      if (this.selectedCall && this.selectedCall.id === updatedCall.id) {
        this.selectedCall = updatedCall
      }

      // Refresh the grid to show updated call
      if (this.$refs.callHistoryCard?.gridApi) {
        this.$refs.callHistoryCard.gridApi.refreshServerSide({ purge: false })
      }

      this.toast.success('Call summary generated successfully')
    },
    openConfigModal(configId) {
      // New configuration
      if (configId === null) {
        this.selectedConfig = null
        this.isConfigModalOpen = true
        return
      }

      const config = this.configurations.find((c) => c.id === configId)
      if (!config) {
        this.toast.error('Configuration not found')
        return
      }

      this.selectedConfig = config
      this.isConfigModalOpen = true
    },
    async handleSaveConfig(config) {
      try {
        if (!config.id) {
          config.tenant = 1     // Hardcode tenant ID until auth is implemented
        }

        const isUpdate = !!config.id

        if (isUpdate) {
          await axios.patch(`api/outreach/call-configurations/${config.id}/`, config)
          this.toast.success('Configuration updated successfully')
        } else {
          await axios.post('api/outreach/call-configurations/', config)
          this.toast.success('Configuration created successfully')
        }

        const response = await axios.get('api/outreach/call-configurations/')
        this.configurations = response.data.results || response.data

        this.selectedConfig = null
        this.isConfigModalOpen = false
      } catch (error) {
        console.error('Failed to save configuration:', error)
        this.toast.error('Failed to save configuration. Please try again.')
      }
    },
  },
}
</script>
